<template>
    <modal
      event-tracking-source="sign up popup"
      :hasClose="true"
      @close="openModal(false)"
    >
        <div class="flex py-6 sm:py-0 w-full h-full overflow-y-auto">
            <div class="flex-col hidden left-side sm:p-6 sm:pr-6 sm:pb-0 lg:pb-0 lg:p-12 lg:pr-24 rounded-l-xl md:flex">
                <div
                  class="text-[40px] font-bold text-primary-purple leading-[50px]"
                >
                  Start <span class="font-garamond text-[50px]">discovering</span><br>Motion Array for <span class="font-garamond text-[50px]">free</span>
                </div>
                <ul class="mt-2 list-none mb-0 pl-0 text-base features-ul">
                    <li class="flex items-center">
                      <i class="fa-regular fa-check"></i>
                      <span>100s of free video templates</span>
                    </li>
                    <li class="flex items-center">
                      <i class="fa-regular fa-check"></i>
                      <span>100s of free songs &amp; SFX</span>
                    </li>
                    <li class="flex items-center">
                      <i class="fa-regular fa-check"></i>
                      <span>Dozens of free graphic assets</span>
                    </li>
                    <li class="flex items-center">
                      <i class="fa-regular fa-check"></i>
                      <span>New free assets sent to your email regularly</span>
                    </li>
                    <li class="flex items-center">
                      <i class="fa-regular fa-check"></i>
                      <span>A catalog of 2M+ video templates, songs, <br> motion graphics &amp; more to explore</span>
                    </li>
                </ul>
                <img
                  class="mt-auto mb-10"
                  style="max-width: 410px;"
                  :src="getAssetPath('signup_v4.png')"
                />
            </div>
            <div class="w-full pt-3 sm:py-4 lg:py-12 px-4 sm:px-6 lg:px-12 right-side sm:rounded-r-xl sm:w-auto text-primary-purple">
                <div
                  class="text-3xl sm:text-lg font-semibold md:font-normal"
                >
                  Create a free trial account
                </div>
                <div class="flex justify-center mt-3 sm:mt-8">
                    <button
                      type="button"
                      @click="proxyRedirect('facebook')"
                      class="px-0 social-btn al-btn al-btn--primary-dark-outline sm:px-8 lg:px-12 mr-1 font-bold"
                    >
                        <i data-v-fb69bce4="" class="fa fa-facebook mr-2"></i>
                        Facebook
                    </button>
                    <button
                      type="button"
                      @click="proxyRedirect('google')"
                      class="px-0 mr-2 font-bold social-btn al-btn al-btn--primary-dark-outline sm:mr-2 sm:px-8 lg:px-12 ml-1"
                    >
                        <SvgIcon icon="google-al" class="inline-flex" />
                        Google
                    </button>
                </div>
                <div class="relative z-10 mt-4 text-sm text-center or sm:mt-6 sm:pt-1">
                    <span class="px-4 bg-primary-beige">or</span>
                </div>
                <p class="mt-2 text-center text-red-500" v-if="globalError.length > 0" v-html="globalError"></p>
                <form @submit.prevent="submitForm">
                    <div class="relative mt-4 sm:mt-3">
                        <floating-input
                          type="text"
                          v-model="signUpData.full_name"
                          label="Full name"
                          :showValidIcon="isValidName"
                          :error="errors.full_name || ''"
                        />
                    </div>
                    <div class="relative mt-4 sm:mt-6">
                        <floating-input
                          type="email"
                          v-model="signUpData.email"
                          label="Email"
                          :showValidIcon="isValidEmail"
                          :error="errors.email || ''"
                        />
                    </div>
                    <div class="relative mt-4 sm:mt-6">
                        <floating-input
                          type="password"
                          v-model="signUpData.password"
                          label="Password"
                          :showValidIcon="isValidPassword"
                          :error="errors.password || ''"
                        />
                    </div>
                    <div class="mt-4 sm:mt-10">
                        <div class="text-xs text-primary-purple sm:text-sm ml-4">No credit card required</div>
                        <div class="mt-2 sm:mt-5">
                          <LoaderButton
                            v-event-track:signupButtonClicked
                            :disabled="isSubmitting"
                            :is-loading="isSubmitting"
                          >
                            Sign Up Free
                          </LoaderButton>
                        </div>
                        <vue-recaptcha
                          ref="recaptcha"
                          @verify="onCaptchaVerified"
                          @expired="onCaptchaExpired"
                          size="invisible"
                          :sitekey="recaptchaSiteKey">
                        </vue-recaptcha>
                    </div>
                    <div class="mt-3">
                        <span
                          class="text-sm"
                          style="color: rgba(0, 1, 29, 0.50);"
                        >
                          Already a member?
                        </span>
                        <a
                          href="/account/login/"
                          class="text-sm text-primary-purple hover:text-primary-purple"
                          @click.prevent="openSignInModal('Sign In')"
                        >
                          Sign In
                        </a>
                    </div>
                    <p class="mt-3 sm:mt-8 pb-3 sm:pb-0 text-xs text-center" style="color: rgba(0, 1, 29, 0.50);">
                      By registering, you agree to the
                      <a
                        href="/terms-of-service/"
                        class="text-primary-purple hover:text-primary-purple"
                      >
                        Terms of Use
                      </a>
                      and
                      <a
                        href="/privacy-policy/"
                        class="text-primary-purple hover:text-primary-purple"
                      >
                        Privacy Policy
                      </a>
                    </p>
                </form>
            </div>
        </div>
    </modal>
</template>

<script>
import Modal from './Modal'
import VueRecaptcha from 'vue-recaptcha'
import SvgIcon from '@/components/SvgIcon'
import FloatingInput from './Fields/FloatingInput'
import SocialSignOn from '@/mixins/SocialSignOn'
import analyticsEvents from "@/utils/analyticsEvents";
import { eventTracker } from '@/utils/eventTracker';
import { getAssetPath } from "@/utils/contentDelivery";
import { validateFullName } from "@/utils";
import LoaderButton from './LoaderButton.vue';

const MAX_FULL_NAME_LENGTH = 300;
const MAX_EMAIL_LENGTH = 70;
const MAX_LIMIT = 'Characters limit exceeded, please use a shorter version.';

export default {
  components: {
    Modal,
    VueRecaptcha,
    SvgIcon,
    FloatingInput,
    LoaderButton,
  },
  mixins: [
    SocialSignOn,
  ],
  props: {
    socialError: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      signUpData: {
          full_name: '',
          email: '',
          password: '',
          recaptchaToken: '',
      },
      errors: {}, // Contains errors separately for each field
      globalError: '', // Global Error independent of field
      recaptchaSiteKey: process.env.MIX_RECAPTCHA_SITE_KEY,
      isSubmitting: false,
    }
  },
  mounted() {
    if (typeof window !== 'undefined') {
      // Load recaptcha script
      const script = document.createElement('script')
      script.src = 'https://www.google.com/recaptcha/api.js?onload=vueRecaptchaApiLoaded&render=explicit';
      script.async = true
      document.body.appendChild(script)
    }
  },
  computed: {
    isValidName() {
      return validateFullName(this.signUpData.full_name);
    },
    isValidEmail() {
      // max length 70 - MAX_EMAIL_LENGTH
      const re = /^(?!.{71})\S+@\S+\.\S+/;
      return re.test(this.signUpData.email)
    },
    isValidPassword() {
      // Checks if password fulfils all  following policies.
      // - equal or more than 8 characters
      // - must contain uppercase letter
      // - must contain lowercase letter
      // - must contain number
      if (
          this.signUpData.password.length < 6 ||
          this.signUpData.password.length > 50
      ) {
          return false
      }
      return true
    },
  },
  methods: {
    validate() {
      this.errors = {}
      if (!this.isValidName) {
        if (this.signUpData.full_name?.length > MAX_FULL_NAME_LENGTH) {
          this.errors.full_name = MAX_LIMIT;
        } else {
          this.errors.full_name = 'Please enter your full name.'
        }
      }
      if (!this.isValidEmail) {
        if (this.signUpData.email?.length > MAX_EMAIL_LENGTH) {
          this.errors.email = MAX_LIMIT;
        } else {
        this.errors.email = 'Please enter a valid email address.'
        }
      }
      if (!this.isValidPassword) {
        this.errors.password = `Password must be at least 6 characters long (up to 50 characters supported).`;
      }
      if (Object.keys(this.errors).length > 0) {
        return false
      }

      return true
    },
    submitForm() {
      // Validation
      if(this.validate()) {
        this.isSubmitting = true;

        // Execute recaptcha after validation passed
        this.$refs.recaptcha.execute()
      }
    },

    proxyRedirect(provider){
      eventTracker(analyticsEvents.clickOnSocialRegister,{provider});

      let rememberMe = true; // there is no remember_me checkbox on signup form.

      this.redirectTo(provider, rememberMe);
    },

    saveUrl() {
      const isBrowse = window.location.pathname.startsWith('/browse')
      const categorySlugs = window.__MA__.browse_category_filters.categories.map((c) => c.slug)
      const isAsset = categorySlugs.some((slug) => {
        return window.location.pathname.startsWith(`/${slug}`)
      })

      if (!isBrowse && !isAsset) {
        return
      }

      sessionStorage.setItem("redirectTo", window.location.href);
    },

    sendRequest() {
      // New design does not have terms & conditions checkbox,
      // We are faking it
      const data = {terms: true, ...this.signUpData}
      axios.post('/api/sign-up/free', data).then(response => {
        eventTracker(analyticsEvents.register, {
          user_id: response.data.user_id,
          method: 'email',
          registration_method: 'email',
          user_agent: navigator.userAgent,
          referred_url: document.referrer,
        });
            if (response.data && response.data.redirect) {
                if (response.data.redirect.includes('thank-you')) {
                  this.saveUrl()
                }
                window.location.href = response.data.redirect
            }
        }).catch(error => {
            this.$refs.recaptcha.reset();
            this.isSubmitting = false;
            const data = error.response.data;
            this.errors = {} // Somehow if we don't set it as empty, vue doesn't watch the change

            let reason = 'multiple errors';
            if (typeof data.errors !== 'undefined') {
              Object.keys(data.errors).forEach(key => {
                this.errors[key] = data.errors[key][0]
              })
            } else if (typeof data.message !== 'undefined') {
              reason = this.globalError = data.message
            }

        eventTracker(analyticsEvents.registrationFailed, {
          reason,
          registration_method: 'email',
          user_agent: navigator.userAgent,
          referred_url: document.referrer,
        });

        })
    },
    openModal(open = true) {
      this.$store.dispatch('setOpenSignUpModal', { open, clickSource: 'sign up popup' });
      this.$emit('toggle', open)
    },
    openSignInModal(buttonName) {
      this.openModal(false)
      this.$nextTick(() => {
        this.$store.dispatch('setOpenSignInModal', { open, clickSource: 'sign up popup', buttonName })
      })
    },
    onCaptchaVerified: function (token) {
        this.signUpData.recaptchaToken = token
        this.sendRequest()
    },
    onCaptchaExpired: function () {
        this.$refs.recaptcha.reset()
    },
    getAssetPath,
  },
  watch: {
    'signUpData.full_name'() {
      if(this.isValidName) {
        delete this.errors.full_name
      }
    },
    'signUpData.email'() {
      if(this.isValidEmail) {
        delete this.errors.email
      }
    },
    'signUpData.password'() {
      if(this.isValidPassword) {
        delete this.errors.password
      }
    },
    socialError(newVal) {
      this.globalError = newVal;
    },
  }
}
</script>

<style scoped>
.text-\[\#534cda\] {
  color: #534cda;
}
.text-\[\#808080\] {
  color: #808080;
}
.font-artlist {
  font-family: 'Artlist Sans';
}
.al-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 44px;
    padding-left: 45px;
    padding-right: 45px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 15px;
    transition: all 250ms;
    border: none;
}
.al-btn--primary-dark {
  @apply bg-primary-purple text-primary-beige;
}
.al-btn--primary-dark-outline {
  @apply border border-solid border-primary-purple bg-transparent text-primary-purple;
}
.al-btn--primary-dark-outline:hover {
  @apply bg-primary-purple text-primary-beige;
}
.al-btn--primary {
  @apply bg-primary-purple text-primary-beige;
}
.al-btn--primary:hover {
  @apply bg-primary-dark-purple;
}

.features-ul li {
    @apply mt-3 text-primary-purple;
    font-variant-numeric: lining-nums;
}
.features-ul i {
    @apply pr-3 text-base;
}
.social-btn {
    width: 155px;
    transition: 0.3s all;
}
.social-btn:hover {
  opacity: 0.9;
}
.or {
  @apply text-primary-purple;
}

.text-\[40px\] {
  font-size: 40px;
}
.text-\[50px\] {
  font-size: 50px;
}
.leading-\[50px\] {
  line-height: 50px;
}


.or:before {
    content: '';
    width: 100%;
    display: block;
    height: 1px;
    background-color: rgba(0, 1, 29, 0.20);
    position: absolute;
    top: 50%;
    z-index: -1;
}
.text-4d4d4d {
  color: #4d4d4d;
}

@screen sm {
  .social-btn {
        width: 180px;
    }
    .right-side {
        max-width: 500px;
    }
}
@media (orientation: landscape) and (max-height: 480px) {
  .left-side {
    display: none;
  }
}

/* Modal styling */
.modal-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 42px;
  z-index: 999;
  overflow-y: auto;
}
>>> .modal-container {
    position: relative;
    margin: 0;
    padding: 0;
    max-width: 1100px;
    border-radius: 0;
    transition: all 0.3s ease;
    width: 100%;
    height: 100%;
    margin-top: 6px;
}
>>> .modal-body {
    height: auto;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    @apply bg-primary-beige;
    border-radius: 12px;
}
>>> .modal-container {
    margin-top: 0;
    padding-top: 6px;
}
>>> .close {
    top: 16px !important;
    right: 24px !important;
}
>>> .close:before,
>>> .close:after {
      background: #828282 !important;
      width: 24px !important;
    }
@screen sm {
    >>> .close {
        right: -20px !important;
        top: -20px !important;
    }
    >>> .close:before,
    >>> .close:after {
      background: white !important;
    }
    >>> .modal-container {
        width: auto;
        height: auto;
        border-radius: 12px;
        overflow-y: initial;
        max-height: 100%;
        margin-top: 6px;
        padding-top: 0;
    }
    .modal-mask {
        margin-top: 0;
        background-color: rgba(0,0,0,0.8);
    }
}
@media (orientation: landscape) and (max-height: 480px) {
  .modal-mask {
    z-index: 1000;
  }
  >>> .modal-container {
    height: 100%;
    border-radius: 0;
    margin-top: 0;
  }
  >>> .close {
    top: 0 !important;
    right: -24px !important;
  }
}
</style>
